import JSZip from 'jszip';

/**
 * Generowanie plikow wsadowych do DFG (dfg.ufg.pl).
 *
 * Obslugiwane komunikaty:
 *  - ZAWMRP - rejestracja mieszkaniowego rachunku powierniczego (poziom inwestycji)
 *  - ZAUMDE - zgloszenie umowy deweloperskiej (poziom lokalu)
 *
 * Reguly formatu pliku (ze specyfikacji DFG):
 *  JSON, UTF-8, spakowany do ZIP bez hasla, max 1 MB, max 1000 rekordow,
 *  nazwa pliku max 49 znakow lacznie z ".zip", bez polskich znakow,
 *  tablica [ ] z rekordami { }.
 */

export const DFG_MAX_RECORDS = 1000;
export const DFG_MAX_FILE_BYTES = 1024 * 1024;
export const DFG_MAX_FILENAME_LENGTH = 49;

export const LEGAL_STATUS_OPTIONS = [
  'Własność',
  'Użytkowanie wieczyste',
  'Współwłasność',
];

/* ------------------------------------------------------------------ */
/* Konwersje i parsowanie danych z Sovo Pay                            */
/* ------------------------------------------------------------------ */

/**
 * NRB "50 1140 1124 6631 0200 0000 0004" -> IBAN "PL50114011246631020000000004"
 */
export const toIban = (raw) => {
  if (!raw) return '';
  const compact = String(raw).replace(/[\s-]/g, '').toUpperCase();
  if (!compact) return '';
  return compact.startsWith('PL') ? compact : `PL${compact}`;
};

export const isValidIban = (value) =>
  /^PL\d{26}$/.test(String(value || '').replace(/[\s-]/g, '').toUpperCase());

/**
 * Waliduje date w formacie YYYY-MM-DD wraz z istnieniem dnia w miesiacu.
 * Odrzuca np. "2023-06-31" (czerwiec ma 30 dni).
 */
export const isValidDate = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))) return false;
  const [y, m, d] = String(value).split('-').map(Number);
  if (m < 1 || m > 12 || d < 1) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === m - 1 &&
    dt.getUTCDate() === d
  );
};

/**
 * Normalizuje wartosc daty do "RRRR-MM-DD" dla pola <input type="date">.
 * Przyjmuje string, Date oraz Timestamp z Firestore (duck-typing po .toDate).
 */
export const toDateInputValue = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const match = value.match(/^\d{4}-\d{2}-\d{2}/);
    return match ? match[0] : '';
  }
  const date = typeof value.toDate === 'function' ? value.toDate() : value;
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * "ul. Dabrowskiego 9, Szczecin"        -> { street: 'Dabrowskiego', buildingNumber: '9', city: 'Szczecin' }
 * "ul. Piekary 7, 61-823 Poznan"        -> { ..., zipCode: '61-823', city: 'Poznan' }
 */
export const parseLocation = (location) => {
  const out = { street: '', buildingNumber: '', zipCode: '', city: '' };
  if (!location) return out;

  const parts = String(location).split(',').map((s) => s.trim()).filter(Boolean);
  const streetPart = parts[0] || '';
  const cityPart = parts.slice(1).join(', ');

  const match = streetPart.match(
    /^(?:ul\.|al\.|os\.|pl\.|ulica|aleja)?\s*(.*?)\s+(\d+[A-Za-z]?(?:\/\d+[A-Za-z]?)?)$/
  );
  if (match) {
    out.street = match[1].trim();
    out.buildingNumber = match[2];
  } else {
    out.street = streetPart.replace(/^(?:ul\.|al\.|os\.|pl\.)\s*/i, '').trim();
  }

  const zip = cityPart.match(/(\d{2}-\d{3})/);
  if (zip) out.zipCode = zip[1];
  out.city = cityPart.replace(/\d{2}-\d{3}/, '').replace(/\s+/g, ' ').trim();

  return out;
};

/**
 * "A4" -> "4". Gdy kod nie zawiera cyfr, zwraca kod bez zmian.
 */
export const flatCodeToApartmentNumber = (code) => {
  if (!code) return '';
  const digits = String(code).match(/\d+[A-Za-z]?$/);
  return digits ? digits[0] : String(code);
};

/**
 * Rowny podzial 100% na n etapow, reszta dopisana do ostatniego etapu.
 */
export const evenPercentages = (n) => {
  const count = Number(n) || 0;
  if (count < 1) return [];
  const base = Math.floor((100 / count) * 100) / 100;
  const arr = new Array(count).fill(base);
  const sum = Number((base * count).toFixed(2));
  arr[count - 1] = Number((base + (100 - sum)).toFixed(2));
  return arr;
};

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const stripPolishChars = (text) =>
  String(text || '')
    .replace(/ł/g, 'l')
    .replace(/Ł/g, 'L')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9_-]/g, '');

/* ------------------------------------------------------------------ */
/* Nazwa pliku                                                         */
/* ------------------------------------------------------------------ */

/**
 * Buduje nazwe zgodna z regulami DFG: {TYP}_{Nazwa}_{DDMMRRRR}[_{nr}], bez
 * polskich znakow, przyciete tak, by z rozszerzeniem zmiescic sie w 49 znakach.
 */
export const buildDfgFilename = (messageType, label, dateStr, sequence, extension = '.zip') => {
  const datePart = isValidDate(dateStr) ? dateStr.split('-').reverse().join('') : '';
  const seqPart = sequence ? `_${stripPolishChars(sequence)}` : '';
  const suffix = `${datePart ? `_${datePart}` : ''}${seqPart}${extension}`;

  // -1 na podkreslnik oddzielajacy etykiete od typu komunikatu
  const budget = DFG_MAX_FILENAME_LENGTH - messageType.length - suffix.length - 1;
  const labelPart = budget > 0 ? stripPolishChars(label).slice(0, budget) : '';

  return `${messageType}${labelPart ? `_${labelPart}` : ''}${suffix}`;
};

/* ------------------------------------------------------------------ */
/* Wstepne wypelnienie formularzy danymi z Sovo Pay                    */
/* ------------------------------------------------------------------ */

/**
 * ZAWMRP: liczba etapow z stages_total, procenty rozdzielone rowno,
 * terminy z stageCompletionHistory jesli sa zapisane.
 */
export const prefillZawmrpForm = (investment) => {
  const saved = investment?.dfg || {};
  const total = Number(investment?.stages_total) || 0;
  const history = Array.isArray(investment?.stageCompletionHistory)
    ? investment.stageCompletionHistory
    : [];

  const percentages = evenPercentages(total);
  const schedules =
    total > 0
      ? Array.from({ length: total }, (_, i) => {
          const stage = i + 1;
          const entry = history.find((h) => Number(h?.stageNumber) === stage);
          return {
            stage,
            percentageCost: percentages[i],
            deadline: toDateInputValue(entry?.paymentDueDate),
          };
        })
      : [{ stage: 1, percentageCost: '', deadline: '' }];

  return {
    mrpAccountNumber: saved.mrpAccountNumber || '',
    buildingPermits: saved.buildingPermits?.length
      ? saved.buildingPermits
      : [{ permitNumber: '', authorityName: '' }],
    schedules: saved.schedules?.length ? saved.schedules : schedules,
    developerParcels: saved.developerParcels?.length
      ? saved.developerParcels
      : [{ landRegisterNumber: '', area: '', legalStatus: 'Własność' }],
  };
};

/**
 * Rozbija kwote zaliczki lokalu na transze wg harmonogramu inwestycji (dfg.schedules).
 * Roznica z zaokraglen trafia do ostatniej transzy, zeby suma zgadzala sie co do grosza.
 */
export const prefillPaymentScheduling = (flat, investment) => {
  const total = Number(flat?.an_amount) || 0;
  const schedules = investment?.dfg?.schedules || [];

  if (schedules.length === 0 || total === 0) {
    return [{ amount: total || '', paymentDate: '' }];
  }

  const rows = schedules.map((s) => ({
    amount: round2((total * (Number(s.percentageCost) || 0)) / 100),
    paymentDate: s.deadline || '',
  }));

  const diff = round2(total - round2(rows.reduce((acc, r) => acc + r.amount, 0)));
  if (diff !== 0) rows[rows.length - 1].amount = round2(rows[rows.length - 1].amount + diff);

  return rows;
};

/**
 * ZAUMDE: subkonto z account_number lokalu, adres z location inwestycji
 * uzupelniony o czesci wspolne zapisane wczesniej (dfg.address).
 */
export const prefillZaumdeForm = (flat, investment) => {
  const saved = flat?.dfg || {};
  const parsed = parseLocation(investment?.location);
  const shared = investment?.dfg?.address || {};

  return {
    mrpAccountNumber: saved.mrpAccountNumber || investment?.dfg?.mrpAccountNumber || '',
    subAccountNumber: saved.subAccountNumber || toIban(flat?.account_number),
    transferOwnershipDate: saved.transferOwnershipDate || '',
    paymentScheduling: saved.paymentScheduling?.length
      ? saved.paymentScheduling
      : prefillPaymentScheduling(flat, investment),
    propertyAddress: saved.propertyAddress?.length
      ? saved.propertyAddress
      : [
          {
            landRegisterNumber: '',
            street: shared.street || parsed.street || '',
            buildingNumber: shared.buildingNumber || parsed.buildingNumber || '',
            apartmentNumber: flatCodeToApartmentNumber(flat?.code),
            zipCode: shared.zipCode || parsed.zipCode || '',
            city: shared.city || parsed.city || '',
            district: shared.district || '',
            commune: shared.commune || '',
            province: shared.province || '',
          },
        ],
  };
};

/* ------------------------------------------------------------------ */
/* Budowa rekordow                                                     */
/* ------------------------------------------------------------------ */

const hasAnyValue = (obj, keys) => keys.some((k) => String(obj?.[k] ?? '').trim() !== '');

export const buildZawmrpRecord = (form) => {
  const permits = (form.buildingPermits || [])
    .filter((p) => hasAnyValue(p, ['permitNumber', 'authorityName']))
    .map((p) => ({
      permitNumber: String(p.permitNumber || '').trim(),
      authorityName: String(p.authorityName || '').trim(),
    }));

  const schedules = (form.schedules || [])
    .filter((s) => hasAnyValue(s, ['stage', 'percentageCost', 'deadline']))
    .map((s) => ({
      stage: Number(s.stage),
      percentageCost: Number(s.percentageCost),
      deadline: String(s.deadline || '').trim(),
    }));

  const parcels = (form.developerParcels || [])
    .filter((p) => hasAnyValue(p, ['landRegisterNumber', 'area', 'legalStatus']))
    .map((p) => ({
      landRegisterNumber: String(p.landRegisterNumber || '').trim(),
      area: round2(p.area),
      legalStatus: String(p.legalStatus || '').trim(),
    }));

  return {
    mrpAccountNumber: toIban(form.mrpAccountNumber),
    buildingPermits: permits,
    schedules,
    developerParcels: parcels,
  };
};

export const buildZaumdeRecord = (form) => {
  const payments = (form.paymentScheduling || [])
    .filter((p) => hasAnyValue(p, ['amount', 'paymentDate']))
    .map((p) => ({
      amount: round2(p.amount),
      paymentDate: String(p.paymentDate || '').trim(),
    }));

  const addresses = (form.propertyAddress || [])
    .filter((a) => hasAnyValue(a, ['landRegisterNumber', 'street', 'buildingNumber', 'city']))
    .map((a) => {
      const entry = {
        landRegisterNumber: String(a.landRegisterNumber || '').trim(),
        street: String(a.street || '').trim() || null,
        buildingNumber: String(a.buildingNumber || '').trim(),
        zipCode: String(a.zipCode || '').trim(),
        city: String(a.city || '').trim(),
        district: String(a.district || '').trim(),
        commune: String(a.commune || '').trim(),
        province: String(a.province || '').trim(),
      };
      const apartment = String(a.apartmentNumber || '').trim();
      if (apartment) entry.apartmentNumber = apartment;
      return entry;
    });

  return {
    mrpAccountNumber: toIban(form.mrpAccountNumber),
    developmentAgreement: {
      subAccountNumber: toIban(form.subAccountNumber),
      transferOwnershipDate: String(form.transferOwnershipDate || '').trim(),
    },
    paymentScheduling: payments,
    propertyAddress: addresses,
  };
};

/* ------------------------------------------------------------------ */
/* Walidacja                                                           */
/* ------------------------------------------------------------------ */

export const validateZawmrp = (form) => {
  const errors = [];

  if (!String(form.mrpAccountNumber || '').trim()) {
    errors.push('Podaj glowny rachunek MRP.');
  } else if (!isValidIban(toIban(form.mrpAccountNumber))) {
    errors.push('Glowny rachunek MRP musi byc w formacie IBAN: PL + 26 cyfr.');
  }

  const permits = (form.buildingPermits || []).filter((p) =>
    hasAnyValue(p, ['permitNumber', 'authorityName'])
  );
  if (permits.length === 0) errors.push('Dodaj co najmniej jedno pozwolenie na budowe.');
  permits.forEach((p, i) => {
    if (!String(p.permitNumber || '').trim()) errors.push(`Pozwolenie ${i + 1}: brak numeru.`);
    if (!String(p.authorityName || '').trim()) errors.push(`Pozwolenie ${i + 1}: brak organu wydajacego.`);
  });

  const schedules = (form.schedules || []).filter((s) =>
    hasAnyValue(s, ['stage', 'percentageCost', 'deadline'])
  );
  if (schedules.length === 0) errors.push('Dodaj co najmniej jeden etap harmonogramu.');
  schedules.forEach((s, i) => {
    if (!Number(s.stage)) errors.push(`Etap ${i + 1}: brak numeru etapu.`);
    if (!(Number(s.percentageCost) > 0)) errors.push(`Etap ${i + 1}: procent kosztow musi byc wiekszy od zera.`);
    if (!isValidDate(s.deadline)) errors.push(`Etap ${i + 1}: nieprawidlowy termin (wymagany format RRRR-MM-DD).`);
  });
  if (schedules.length > 0) {
    const sum = Number(
      schedules.reduce((acc, s) => acc + (Number(s.percentageCost) || 0), 0).toFixed(2)
    );
    if (sum !== 100) errors.push(`Procenty etapow sumuja sie do ${sum}%, a powinny do 100%.`);
  }

  const parcels = (form.developerParcels || []).filter((p) =>
    hasAnyValue(p, ['landRegisterNumber', 'area', 'legalStatus'])
  );
  if (parcels.length === 0) errors.push('Dodaj co najmniej jedna dzialke deweloperska.');
  parcels.forEach((p, i) => {
    if (!String(p.landRegisterNumber || '').trim()) errors.push(`Dzialka ${i + 1}: brak numeru ksiegi wieczystej.`);
    if (!(Number(p.area) > 0)) errors.push(`Dzialka ${i + 1}: powierzchnia musi byc wieksza od zera.`);
    if (!String(p.legalStatus || '').trim()) errors.push(`Dzialka ${i + 1}: brak stanu prawnego.`);
  });

  return errors;
};

export const validateZaumde = (form) => {
  const errors = [];

  if (!String(form.mrpAccountNumber || '').trim()) {
    errors.push('Podaj glowny rachunek MRP (ten sam co w ZAWMRP inwestycji).');
  } else if (!isValidIban(toIban(form.mrpAccountNumber))) {
    errors.push('Glowny rachunek MRP musi byc w formacie IBAN: PL + 26 cyfr.');
  }

  if (!String(form.subAccountNumber || '').trim()) {
    errors.push('Podaj subkonto nabywcy.');
  } else if (!isValidIban(toIban(form.subAccountNumber))) {
    errors.push('Subkonto nabywcy musi byc w formacie IBAN: PL + 26 cyfr.');
  }

  if (!isValidDate(form.transferOwnershipDate)) {
    errors.push('Nieprawidlowa data przeniesienia wlasnosci (wymagany format RRRR-MM-DD).');
  }

  const payments = (form.paymentScheduling || []).filter((p) =>
    hasAnyValue(p, ['amount', 'paymentDate'])
  );
  if (payments.length === 0) errors.push('Dodaj co najmniej jedna transze harmonogramu wplat.');
  payments.forEach((p, i) => {
    if (!(Number(p.amount) > 0)) errors.push(`Transza ${i + 1}: kwota musi byc wieksza od zera.`);
    if (!isValidDate(p.paymentDate)) errors.push(`Transza ${i + 1}: nieprawidlowy termin (wymagany format RRRR-MM-DD).`);
  });

  const addresses = (form.propertyAddress || []).filter((a) =>
    hasAnyValue(a, ['landRegisterNumber', 'street', 'buildingNumber', 'city'])
  );
  if (addresses.length === 0) errors.push('Uzupelnij adres nieruchomosci.');
  addresses.forEach((a, i) => {
    const prefix = addresses.length > 1 ? `Adres ${i + 1}: ` : '';
    if (!String(a.landRegisterNumber || '').trim()) errors.push(`${prefix}brak numeru ksiegi wieczystej.`);
    if (!String(a.buildingNumber || '').trim()) errors.push(`${prefix}brak numeru budynku.`);
    if (!/^\d{2}-\d{3}$/.test(String(a.zipCode || '').trim())) errors.push(`${prefix}nieprawidlowy kod pocztowy (format 00-000).`);
    if (!String(a.city || '').trim()) errors.push(`${prefix}brak miasta.`);
    if (!String(a.district || '').trim()) errors.push(`${prefix}brak dzielnicy.`);
    if (!String(a.commune || '').trim()) errors.push(`${prefix}brak gminy.`);
    if (!String(a.province || '').trim()) errors.push(`${prefix}brak wojewodztwa.`);
  });

  return errors;
};

/* ------------------------------------------------------------------ */
/* Pakowanie i pobranie pliku                                          */
/* ------------------------------------------------------------------ */

export const serializeRecords = (records) => JSON.stringify(records, null, 2);

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const assertRecords = (list, filename) => {
  if (list.length === 0) throw new Error('Brak rekordow do wygenerowania.');
  if (list.length > DFG_MAX_RECORDS) {
    throw new Error(`Plik moze zawierac max ${DFG_MAX_RECORDS} rekordow (jest ${list.length}).`);
  }
  if (filename.length > DFG_MAX_FILENAME_LENGTH) {
    throw new Error(`Nazwa pliku ma ${filename.length} znakow, limit to ${DFG_MAX_FILENAME_LENGTH}.`);
  }
};

/**
 * Pakuje rekordy do ZIP-a i uruchamia pobranie w przegladarce.
 * To jest plik wgrywany na portal DFG. Zwraca { filename, jsonBytes, zipBytes }.
 */
export const downloadDfgZip = async (records, zipFilename) => {
  const list = Array.isArray(records) ? records : [records];
  assertRecords(list, zipFilename);

  const json = serializeRecords(list);
  const jsonName = zipFilename.replace(/\.zip$/i, '.json');

  const zip = new JSZip();
  zip.file(jsonName, json);
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });

  if (blob.size > DFG_MAX_FILE_BYTES) {
    throw new Error(`Plik ZIP ma ${(blob.size / 1024).toFixed(0)} kB, limit to 1 MB.`);
  }

  triggerDownload(blob, zipFilename);

  return {
    filename: zipFilename,
    jsonBytes: new Blob([json]).size,
    zipBytes: blob.size,
  };
};

/**
 * Pobiera sam plik JSON, bez pakowania. Na portal DFG idzie ZIP - to jest
 * wersja do wgladu, archiwum i przeslania dalej. Zwraca { filename, bytes }.
 */
export const downloadDfgJson = (records, filename) => {
  const list = Array.isArray(records) ? records : [records];
  const jsonName = filename.replace(/\.zip$/i, '.json');
  assertRecords(list, jsonName);

  const json = serializeRecords(list);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' });
  triggerDownload(blob, jsonName);

  return { filename: jsonName, bytes: blob.size };
};

export default {
  toIban,
  parseLocation,
  flatCodeToApartmentNumber,
  evenPercentages,
  buildDfgFilename,
  buildZawmrpRecord,
  buildZaumdeRecord,
  validateZawmrp,
  validateZaumde,
  downloadDfgZip,
};
